'use client';

import { useTranslation } from '@/lib/i18n';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowUpRight } from 'lucide-react';
import Image from 'next/image';

const APP_STORE_URL = 'https://apps.apple.com/app/id6753872436';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=ai.pythra.app';

interface StoreCardProps {
  label: string;
  url: string;
}

function StoreCard({ label, url }: StoreCardProps) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="rounded-xl bg-white p-3">
        <QRCodeSVG value={url} size={132} level="H" marginSize={0} />
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="group inline-flex h-11 items-center gap-2 rounded-full bg-white px-6 text-[15px] font-medium text-black transition-opacity hover:opacity-90"
      >
        {label}
        <ArrowUpRight className="h-4 w-4 text-violet-500 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </a>
    </div>
  );
}

/**
 * Forge 下载引导页（正式环境 Web 端暂不开放 Forge 时着陆）。
 * 基础版布局，样式后续可自由重设计。
 */
export default function ForgeDownloadPage() {
  const { t } = useTranslation();

  return (
    <div className="relative flex h-dvh w-full items-center overflow-hidden rounded-2xl bg-black">
      {/* 右侧图表底图：仅占右侧，左缘渐变融入纯黑 */}
      <div className="pointer-events-none absolute inset-y-0 right-0 w-full sm:w-[60%]">
        <Image
          src="/image/forge-waitlist2.png"
          alt=""
          fill
          priority
          sizes="(max-width: 640px) 100vw, 60vw"
          className="object-cover object-center"
        />
        <div className="absolute inset-0 bg-linear-to-r from-black via-black/60 to-black/10 sm:via-black/25 sm:to-transparent" />
      </div>

      {/* 左侧文案 + 下载入口 */}
      <div className="relative z-10 flex max-w-xl flex-col items-start px-10 py-12 sm:px-16 lg:px-24">
        <h1 className="text-[60px] font-normal leading-10 tracking-tight text-white">
          Pythra <span className="text-violet-500">Forge</span>
        </h1>

        <p className="mt-12.5 max-w-md text-[16px] leading-7 text-white/60">
          {t('Pythra Forge is currently available only on the app. Download the app to continue.')}
        </p>

        <div className="mt-10 flex flex-wrap items-start gap-8">
          <StoreCard label={t('App Store')} url={APP_STORE_URL} />
          <StoreCard label={t('Google Play')} url={PLAY_STORE_URL} />
        </div>
      </div>
    </div>
  );
}
